import { useEffect, useState } from 'react';

import { CreditCardIcon } from '@heroicons/react/24/outline';
import { debounce } from 'lodash-es';
import { useFormContext } from 'react-hook-form';

import {
  PassengerRead,
  usePassengersListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { FormContextField } from 'src/common/FormField/FormContextField';
import { FormError } from 'src/common/FormField/FormError';
import { Combobox } from 'src/common/primitives/ComboBox';
import { StyledOption } from 'src/common/primitives/ComboBox/StyledOption';
import { SelectOption } from 'src/common/primitives/Select';
import { buildPassengerSelectOption } from 'src/features/add-trip/PassengerInfoFormSection/buildPassengerSelectOption';
import { formatPassengerLabel } from 'src/features/add-trip/PassengerInfoFormSection/formatPassengerLabel';
import { useAuth } from 'src/features/auth/useAuth';

interface ExistingPassengerSearchProps {
  selectedPassenger: PassengerRead | null | undefined;
  onPassengerSelected: (passenger: PassengerRead | undefined) => void;
  disabled?: boolean;
}

export function ExistingPassengerSearch({
  selectedPassenger,
  onPassengerSelected,
  disabled,
}: ExistingPassengerSearchProps) {
  const formContext = useFormContext();
  const auth = useAuth();
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<SelectOption[]>([]);
  const queryState = usePassengersListQuery(
    {
      'Organization-ID': auth.currentOrganizationId || '',
      search: query,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    setOptions(queryState.data?.results?.map(buildPassengerSelectOption) || []);
  }, [queryState.data?.results]);

  useEffect(() => {
    if (selectedPassenger?.id) {
      formContext.clearErrors('passenger.id');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPassenger]);

  const handleQuery = async (query: string) => {
    await setQuery(query);
    return Promise.resolve(
      queryState.data?.results?.map((passenger) => {
        return {
          label: formatPassengerLabel(passenger),
          value: passenger.id,
        };
      }) || []
    );
  };

  const debounceSearch = debounce(handleQuery, 300);

  return (
    <FormContextField
      name="passenger.id"
      rules={{
        required: 'Passenger is required',
      }}
    >
      {({ field, fieldState }) => {
        return (
          <>
            <Combobox
              value={
                (selectedPassenger &&
                  buildPassengerSelectOption(selectedPassenger)) ||
                undefined
              }
              placeholder="Start typing and select a passenger"
              options={options}
              onQueryChange={debounceSearch}
              onChange={(selected) => {
                const passengers = queryState.data?.results || [];
                const passenger = passengers.find(
                  (p) => p.id === selected?.value
                );
                onPassengerSelected(passenger);
                field.onChange(selected?.value);
              }}
              error={fieldState.error?.message?.toString()}
              dataTestId="existing-passenger-search"
              disabled={disabled}
              renderOption={(option, selected, active) => {
                const passengers = queryState.data?.results || [];
                const passenger = passengers.find(
                  (p) => p.id === option?.value
                );

                return (
                  <StyledOption
                    key={option.value}
                    option={option}
                    selected={selected}
                    active={active}
                    startAddornment={
                      passenger &&
                      passenger.has_tilled_account && (
                        <span>
                          <CreditCardIcon className="h-4 w-4" />{' '}
                        </span>
                      )
                    }
                  />
                );
              }}
            />
            {fieldState.error?.message && (
              <FormError error={fieldState.error?.message}></FormError>
            )}
          </>
        );
      }}
    </FormContextField>
  );
}
